import React, { useState, useContext, useEffect, useRef } from "react";
import logoLight from "../../assets/logo.png";
import logoDark from "../../assets/logo-black.png";
import { Link as RouterLink } from "react-router-dom";
import Button from "@material-ui/core/Button";
import CssBaseline from "@material-ui/core/CssBaseline";
import TextField from "@material-ui/core/TextField";
import Link from "@material-ui/core/Link";
import Grid from "@material-ui/core/Grid";
import Typography from "@material-ui/core/Typography";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import { useMediaQuery } from "@material-ui/core";
import { i18n } from "../../translate/i18n";
import { AuthContext } from "../../context/Auth/AuthContext";
import ColorModeContext from "../../layout/themeContext";
import useSettings from "../../hooks/useSettings";
import IconButton from "@material-ui/core/IconButton";
import Brightness4Icon from "@material-ui/icons/Brightness4";
import Brightness7Icon from "@material-ui/icons/Brightness7";
import Visibility from "@material-ui/icons/Visibility";
import VisibilityOff from "@material-ui/icons/VisibilityOff";
import InputAdornment from "@material-ui/core/InputAdornment";
import { Helmet } from "react-helmet";
import BRFlag from "../../assets/brazil.png";
import USFlag from "../../assets/unitedstates.png";
import ESFlag from "../../assets/esspain.png";
import ARFlag from "../../assets/arabe.png";
import { getBackendUrl } from "../../config";
import packageJson from "../../../package.json";

const languageOptions = [
    { value: "pt-BR", label: "Português", icon: BRFlag },
    { value: "en", label: "English", icon: USFlag },
    { value: "es", label: "Español", icon: ESFlag },
    { value: "ar", label: "عربي", icon: ARFlag },
];

const useStyles = makeStyles((theme) => ({
    root: {
        width: "100vw",
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        position: "relative",
        overflow: "hidden",
        background: theme.mode === "dark"
            ? "linear-gradient(160deg, #0d0818 0%, #130a22 50%, #180c2a 100%)"
            : "linear-gradient(160deg, #f5f0ff 0%, #f8f5ff 50%, #ede5ff 100%)",
    },

    // Fondo decorativo — círculos de luz
    bgOrb1: {
        position: "fixed",
        width: 500,
        height: 500,
        borderRadius: "50%",
        background: "radial-gradient(circle, rgba(91,45,142,0.18) 0%, transparent 70%)",
        top: "-150px",
        right: "-100px",
        pointerEvents: "none",
        [theme.breakpoints.down("sm")]: { width: 300, height: 300, top: "-80px", right: "-60px" },
    },
    bgOrb2: {
        position: "fixed",
        width: 400,
        height: 400,
        borderRadius: "50%",
        background: "radial-gradient(circle, rgba(91,45,142,0.12) 0%, transparent 70%)",
        bottom: "-100px",
        left: "-80px",
        pointerEvents: "none",
        [theme.breakpoints.down("sm")]: { width: 250, height: 250 },
    },
    bgOrb3: {
        position: "fixed",
        width: 200,
        height: 200,
        borderRadius: "50%",
        background: "radial-gradient(circle, rgba(91,45,142,0.10) 0%, transparent 70%)",
        top: "50%",
        left: "30%",
        pointerEvents: "none",
    },

    // Layout principal: en desktop split 50/50, en móvil columna
    layout: {
        display: "flex",
        flex: 1,
        minHeight: "100vh",
        [theme.breakpoints.down("sm")]: {
            flexDirection: "column",
        },
    },

    // Panel izquierdo — branding (solo desktop)
    brandPanel: {
        flex: 1,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        padding: "60px 48px",
        position: "relative",
        [theme.breakpoints.down("sm")]: {
            display: "none",
        },
    },

    brandLogo: {
        width: "100%",
        maxWidth: 260,
        height: "auto",
        marginBottom: 32,
        filter: theme.mode === "dark"
            ? "drop-shadow(0 4px 24px rgba(91,45,142,0.30))"
            : "drop-shadow(0 4px 16px rgba(91,45,142,0.15))",
    },

    slideWrapper: {
        textAlign: "center",
        maxWidth: 340,
        minHeight: 90,
        position: "relative",
    },

    slideTitle: {
        fontSize: 20,
        fontWeight: 700,
        color: theme.mode === "dark" ? "rgba(255,255,255,0.90)" : "rgba(15,30,60,0.85)",
        marginBottom: 8,
        lineHeight: 1.3,
    },

    slideDesc: {
        fontSize: 14,
        fontWeight: 400,
        color: theme.mode === "dark" ? "rgba(255,255,255,0.42)" : "rgba(15,30,60,0.42)",
        lineHeight: 1.65,
    },

    slideIcon: {
        fontSize: 36,
        marginBottom: 16,
        display: "block",
    },

    brandDots: {
        display: "flex",
        gap: 8,
        marginTop: 32,
        alignItems: "center",
    },

    brandDot: {
        width: 8,
        height: 8,
        borderRadius: "50%",
        background: "rgba(91,45,142,0.22)",
        cursor: "pointer",
        border: "none",
        padding: 0,
        transition: "all 0.35s ease",
    },

    brandDotActive: {
        background: "#5B2D8E",
        width: 24,
        borderRadius: 4,
        boxShadow: "0 0 8px rgba(91,45,142,0.50)",
    },

    // Panel derecho — formulario
    formPanel: {
        width: 420,
        flexShrink: 0,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        padding: "48px 40px",
        position: "relative",
        background: theme.mode === "dark"
            ? "rgba(20,10,35,0.70)"
            : "rgba(255,255,255,0.80)",
        backdropFilter: "blur(24px)",
        borderLeft: theme.mode === "dark"
            ? "1px solid rgba(91,45,142,0.10)"
            : "1px solid rgba(91,45,142,0.08)",
        [theme.breakpoints.down("sm")]: {
            width: "100%",
            minHeight: "100vh",
            padding: "0",
            background: "transparent",
            backdropFilter: "none",
            borderLeft: "none",
            justifyContent: "flex-start",
        },
    },

    // Card en mobile
    mobileCard: {
        display: "none",
        [theme.breakpoints.down("sm")]: {
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            width: "100%",
            minHeight: "100vh",
            padding: "0 0 40px 0",
        },
    },

    // Header móvil con gradiente
    mobileHeader: {
        display: "none",
        [theme.breakpoints.down("sm")]: {
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            width: "100%",
            padding: "60px 24px 40px",
            background: "linear-gradient(180deg, rgba(91,45,142,0.12) 0%, transparent 100%)",
            borderBottom: theme.mode === "dark"
                ? "1px solid rgba(91,45,142,0.08)"
                : "1px solid rgba(91,45,142,0.10)",
        },
    },

    formInner: {
        width: "100%",
        maxWidth: 380,
        margin: "0 auto",
        [theme.breakpoints.down("sm")]: {
            maxWidth: 420,
            padding: "0 24px",
        },
        [theme.breakpoints.down("xs")]: {
            maxWidth: "100%",
            padding: "0 20px",
        },
    },

    mobileLogo: {
        width: "auto",
        maxWidth: 200,
        height: 56,
        objectFit: "contain",
        marginBottom: 12,
        filter: theme.mode === "dark"
            ? "drop-shadow(0 2px 12px rgba(91,45,142,0.35))"
            : "none",
    },

    mobileSubtitle: {
        fontSize: 13,
        color: theme.mode === "dark" ? "rgba(255,255,255,0.40)" : "rgba(15,30,60,0.40)",
        fontWeight: 500,
        letterSpacing: "0.05em",
    },

    // Contenedor del form
    formContainer: {
        width: "100%",
        maxWidth: 380,
        [theme.breakpoints.down("sm")]: {
            padding: "32px 24px 0",
            maxWidth: "100%",
        },
    },

    formTitle: {
        fontSize: 24,
        fontWeight: 700,
        color: theme.mode === "dark" ? "#f1f5f9" : "#0f1e3c",
        marginBottom: 4,
        [theme.breakpoints.down("sm")]: { fontSize: 20 },
    },

    formSubtitle: {
        fontSize: 13,
        color: theme.mode === "dark" ? "#64748b" : "#94a3b8",
        marginBottom: 28,
        fontWeight: 500,
    },

    form: {
        width: "100%",
        display: "flex",
        flexDirection: "column",
        gap: 12,
    },

    textField: {
        "& .MuiOutlinedInput-root": {
            borderRadius: 12,
            background: theme.mode === "dark" ? "rgba(255,255,255,0.04)" : "rgba(255,255,255,0.90)",
            fontSize: 14,
            transition: "all 0.2s ease",
            "&:hover": {
                background: theme.mode === "dark" ? "rgba(255,255,255,0.07)" : "#fff",
            },
            "&.Mui-focused": {
                background: theme.mode === "dark" ? "rgba(255,255,255,0.07)" : "#fff",
                boxShadow: "0 0 0 3px rgba(91,45,142,0.12)",
            },
            "& fieldset": {
                borderColor: theme.mode === "dark" ? "rgba(91,45,142,0.15)" : "rgba(91,45,142,0.18)",
            },
            "&:hover fieldset": {
                borderColor: "rgba(91,45,142,0.35)",
            },
            "&.Mui-focused fieldset": {
                borderColor: "#5B2D8E",
                borderWidth: 2,
            },
        },
        "& .MuiInputLabel-root": {
            fontSize: 14,
            color: theme.mode === "dark" ? "#475569" : "#94a3b8",
            "&.Mui-focused": { color: "#5B2D8E" },
        },
        "& .MuiInputBase-input": {
            color: theme.mode === "dark" ? "#e2e8f0" : "#0f1e3c",
        },
        "& input:-webkit-autofill, & input:-webkit-autofill:hover, & input:-webkit-autofill:focus": {
            "-webkit-box-shadow": `0 0 0 100px ${theme.mode === "dark" ? "#130a22" : "#ffffff"} inset`,
            "-webkit-text-fill-color": theme.mode === "dark" ? "#e2e8f0" : "#0f1e3c",
            "transition": "background-color 5000s ease-in-out 0s",
            "border-radius": "inherit",
        },
    },

    submitBtn: {
        marginTop: 8,
        height: 48,
        borderRadius: 12,
        background: "linear-gradient(135deg, #5B2D8E, #7C3AED)",
        color: "#fff",
        fontSize: 15,
        fontWeight: 700,
        textTransform: "none",
        letterSpacing: "0.02em",
        boxShadow: "0 4px 16px rgba(91,45,142,0.35)",
        border: "none",
        transition: "all 0.2s ease",
        "&:hover": {
            background: "linear-gradient(135deg, #4a2478, #6d31d4)",
            boxShadow: "0 6px 24px rgba(91,45,142,0.50)",
            transform: "translateY(-1px)",
        },
        "&:active": { transform: "translateY(0)" },
    },

    registerLink: {
        color: "#5B2D8E",
        textDecoration: "none",
        fontWeight: 600,
        fontSize: 13,
        transition: "opacity 0.2s",
        "&:hover": { opacity: 0.75, textDecoration: "none" },
    },

    // Toggle de tema
    themeToggle: {
        position: "absolute",
        top: 16,
        right: 16,
        color: theme.mode === "dark" ? "rgba(255,255,255,0.50)" : "rgba(15,30,60,0.40)",
        "&:hover": { color: "#5B2D8E" },
    },

    // Selector de idioma
    langSelector: {
        position: "fixed",
        top: 16,
        left: 16,
        zIndex: 100,
        display: "flex",
        alignItems: "center",
        gap: 6,
        padding: "6px 10px",
        borderRadius: 8,
        background: theme.mode === "dark" ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.05)",
        border: theme.mode === "dark" ? "1px solid rgba(255,255,255,0.08)" : "1px solid rgba(0,0,0,0.06)",
        cursor: "pointer",
        fontSize: 12,
        fontWeight: 600,
        color: theme.mode === "dark" ? "rgba(255,255,255,0.55)" : "rgba(15,30,60,0.50)",
        userSelect: "none",
        transition: "all 0.2s",
        "&:hover": {
            background: theme.mode === "dark" ? "rgba(91,45,142,0.12)" : "rgba(91,45,142,0.08)",
            color: "#5B2D8E",
        },
    },

    langDropdown: {
        position: "absolute",
        top: "calc(100% + 6px)",
        left: 0,
        background: theme.mode === "dark" ? "#130a22" : "#fff",
        border: theme.mode === "dark" ? "1px solid rgba(91,45,142,0.15)" : "1px solid rgba(0,0,0,0.08)",
        borderRadius: 10,
        boxShadow: "0 8px 24px rgba(0,0,0,0.15)",
        padding: "4px",
        minWidth: 140,
        zIndex: 200,
    },

    langOption: {
        display: "flex",
        alignItems: "center",
        gap: 8,
        padding: "8px 10px",
        borderRadius: 7,
        cursor: "pointer",
        fontSize: 13,
        fontWeight: 500,
        color: theme.mode === "dark" ? "#94a3b8" : "#374151",
        background: "none",
        border: "none",
        width: "100%",
        textAlign: "left",
        transition: "all 0.15s",
        "&:hover": {
            background: "rgba(91,45,142,0.08)",
            color: "#5B2D8E",
        },
    },

    flagIcon: { width: 20, height: 14, borderRadius: 2, objectFit: "cover" },

    versionBadge: {
        position: "fixed",
        bottom: 14,
        right: 16,
        fontSize: 11,
        fontWeight: 500,
        color: theme.mode === "dark" ? "rgba(255,255,255,0.20)" : "rgba(0,0,0,0.22)",
        letterSpacing: "0.04em",
    },

    divider: {
        display: "flex",
        alignItems: "center",
        gap: 10,
        margin: "4px 0",
        "& span": {
            fontSize: 11,
            color: theme.mode === "dark" ? "#334155" : "#cbd5e1",
            fontWeight: 500,
        },
        "&::before, &::after": {
            content: '""',
            flex: 1,
            height: 1,
            background: theme.mode === "dark" ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.07)",
        },
    },
}));

const Login = () => {
    const classes = useStyles();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    const { colorMode } = useContext(ColorModeContext);
    const { appLogoFavicon, appName, mode } = colorMode;
    const [user, setUser] = useState({ email: "", password: "" });
    const [showPassword, setShowPassword] = useState(false);
    const [allowSignup, setAllowSignup] = useState(false);
    const { getPublicSetting } = useSettings();
    const { handleLogin } = useContext(AuthContext);
    const [open, setOpen] = useState(false);
    const ref = useRef();
    const [enabledLanguages, setEnabledLanguages] = useState(["pt-BR", "en", "es"]);
    const [currentSlide, setCurrentSlide] = useState(0);
    const [slideVisible, setSlideVisible] = useState(true);

    // logo.png = blanco (fondos oscuros), logo-black.png = negro (fondos claros)
    const currentLogo = mode === "dark" ? logoLight : logoDark;

    const slides = [
        {
            icon: "💬",
            title: "Gestión inteligente de WhatsApp",
            desc: "Centraliza todas tus conversaciones y atiende a tus clientes sin perder el hilo.",
        },
        {
            icon: "⚡",
            title: "Respuestas más rápidas con IA",
            desc: "Automatiza respuestas frecuentes y ahorra horas de trabajo cada semana.",
        },
        {
            icon: "📊",
            title: "Todo tu equipo en sincronía",
            desc: "Asigna chats, mide resultados y mantén a tu equipo alineado desde un solo lugar.",
        },
    ];

    const goToSlide = (idx) => {
        setSlideVisible(false);
        setTimeout(() => { setCurrentSlide(idx); setSlideVisible(true); }, 320);
    };

    const handleChangeInput = (e) => setUser({ ...user, [e.target.name]: e.target.value });
    const togglePasswordVisibility = () => setShowPassword((v) => !v);
    const handlSubmit = (e) => { e.preventDefault(); handleLogin(user); };

    useEffect(() => {
        const companyId = (() => {
            const p = new URLSearchParams(window.location.search);
            const c = p.get("companyId");
            return c ? parseInt(c) : null;
        })();

        getPublicSetting("userCreation", companyId)
            .then((data) => setAllowSignup(data === "enabled"))
            .catch(() => {});

        getPublicSetting("enabledLanguages", companyId)
            .then((langs) => {
                try { if (langs) setEnabledLanguages(JSON.parse(langs)); }
                catch {}
            })
            .catch(() => {});
    }, []);

    // Cerrar dropdown al click fuera
    useEffect(() => {
        const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
        document.addEventListener("mousedown", handler);
        return () => document.removeEventListener("mousedown", handler);
    }, []);

    // Auto-avance del carrusel
    useEffect(() => {
        const interval = setInterval(() => {
            setSlideVisible(false);
            setTimeout(() => {
                setCurrentSlide((s) => (s + 1) % 3);
                setSlideVisible(true);
            }, 320);
        }, 4000);
        return () => clearInterval(interval);
    }, []);

    const current = languageOptions.find((o) => o.value === i18n.language) || languageOptions[0];

    const handleSelect = (opt) => {
        i18n.changeLanguage(opt.value);
        localStorage.setItem("language", opt.value);
        setOpen(false);
        window.location.reload();
    };


    return (
        <>
            <Helmet>
                <title>{appName || "Octane"}</title>
                {appLogoFavicon && <link rel="icon" href={appLogoFavicon} />}
            </Helmet>
            <CssBaseline />

            <div className={classes.root}>
                {/* Orbes de fondo */}
                <div className={classes.bgOrb1} />
                <div className={classes.bgOrb2} />
                <div className={classes.bgOrb3} />

                {/* Selector de idioma */}
                <div ref={ref} className={classes.langSelector} onClick={() => setOpen((o) => !o)}>
                    <img src={current.icon} alt={current.label} className={classes.flagIcon} />
                    <span>{current.label}</span>
                    <span style={{ fontSize: 9 }}>▾</span>
                    {open && (
                        <div className={classes.langDropdown} onClick={(e) => e.stopPropagation()}>
                            {languageOptions
                                .filter((o) => enabledLanguages.includes(o.value))
                                .map((opt) => (
                                    <button key={opt.value} className={classes.langOption} onClick={() => handleSelect(opt)}>
                                        <img src={opt.icon} alt={opt.label} className={classes.flagIcon} />
                                        {opt.label}
                                    </button>
                                ))}
                        </div>
                    )}
                </div>

                {/* Layout */}
                <div className={classes.layout}>

                    {/* Panel izquierdo — solo desktop */}
                    <div className={classes.brandPanel}>
                        <img src={currentLogo} alt="Octane" className={classes.brandLogo} />
                        <div
                            className={classes.slideWrapper}
                            style={{ opacity: slideVisible ? 1 : 0, transition: "opacity 0.32s ease" }}
                        >
                            <span className={classes.slideIcon}>{slides[currentSlide].icon}</span>
                            <div className={classes.slideTitle}>{slides[currentSlide].title}</div>
                            <div className={classes.slideDesc}>{slides[currentSlide].desc}</div>
                        </div>
                        <div className={classes.brandDots}>
                            {slides.map((_, idx) => (
                                <button
                                    key={idx}
                                    onClick={() => goToSlide(idx)}
                                    className={classes.brandDot}
                                    style={idx === currentSlide ? {
                                        background: "#5B2D8E",
                                        width: 24,
                                        borderRadius: 4,
                                        boxShadow: "0 0 8px rgba(91,45,142,0.50)",
                                    } : {}}
                                />
                            ))}
                        </div>
                    </div>

                    {/* Panel derecho — form */}
                    <div className={classes.formPanel}>
                        {/* Toggle tema */}
                        <IconButton
                            className={classes.themeToggle}
                            onClick={colorMode.toggleColorMode}
                            size="small"
                        >
                            {mode === "dark" ? <Brightness7Icon fontSize="small" /> : <Brightness4Icon fontSize="small" />}
                        </IconButton>

                        {/* Header con logo — solo mobile */}
                        {isMobile && (
                            <div className={classes.mobileHeader}>
                                <img src={currentLogo} alt="Octane" className={classes.mobileLogo} />
                                <Typography
                                    className={classes.mobileSubtitle}
                                    style={{ opacity: slideVisible ? 1 : 0, transition: "opacity 0.32s ease" }}
                                >
                                    {slides[currentSlide].title}
                                </Typography>
                            </div>
                        )}

                        <div className={classes.formInner}>
                            <div className={classes.formContainer}>
                                <Typography className={classes.formTitle}>Bienvenido</Typography>
                                <Typography className={classes.formSubtitle}>Inicia sesión en tu cuenta</Typography>

                                <form className={classes.form} noValidate onSubmit={handlSubmit}>
                                    <TextField
                                        variant="outlined"
                                        required
                                        fullWidth
                                        id="email"
                                        label={i18n.t("login.form.email")}
                                        name="email"
                                        value={user.email}
                                        onChange={handleChangeInput}
                                        autoComplete="email"
                                        autoFocus
                                        className={classes.textField}
                                    />
                                    <TextField
                                        variant="outlined"
                                        required
                                        fullWidth
                                        name="password"
                                        label={i18n.t("login.form.password")}
                                        type={showPassword ? "text" : "password"}
                                        id="password"
                                        value={user.password}
                                        onChange={handleChangeInput}
                                        autoComplete="current-password"
                                        className={classes.textField}
                                        InputProps={{
                                            endAdornment: (
                                                <InputAdornment position="end">
                                                    <IconButton
                                                        onClick={togglePasswordVisibility}
                                                        edge="end"
                                                        style={{ color: mode === "dark" ? "#475569" : "#94a3b8" }}
                                                    >
                                                        {showPassword ? <VisibilityOff /> : <Visibility />}
                                                    </IconButton>
                                                </InputAdornment>
                                            ),
                                        }}
                                    />
                                    <Button
                                        type="submit"
                                        fullWidth
                                        variant="contained"
                                        className={classes.submitBtn}
                                    >
                                        {i18n.t("login.buttons.submit")}
                                    </Button>
                                    {allowSignup && (
                                        <Grid container justifyContent="center" style={{ marginTop: 4 }}>
                                            <Grid item>
                                                <Link component={RouterLink} to="/signup" className={classes.registerLink}>
                                                    {i18n.t("login.buttons.register")}
                                                </Link>
                                            </Grid>
                                        </Grid>
                                    )}
                                </form>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Versión */}
                <Typography className={classes.versionBadge}>v{packageJson.version}</Typography>
            </div>
        </>
    );
};

export default Login;
